import { useState, useRef, useCallback } from 'react';
import {
  Upload,
  FileText,
  Image,
  Download,
  Trash2,
  Eye,
  X,
  Loader2,
  Link,
  HardDrive,
  Tag,
  Pencil,
  ExternalLink,
} from 'lucide-react';
import type { EntityAttachment, CreateEntityAttachmentInput } from '@peles/shared';
import {
  useEntityAttachments,
  useCreateEntityAttachment,
  useUpdateEntityAttachment,
  useDeleteEntityAttachment,
} from '../../hooks/useEntityAttachments';
import { getAttachmentUploadUrl } from '../../api/entity-attachments';
import { uploadFileToS3 } from '../../api/photos';
import { useGoogleDrive } from '../../hooks/useGoogleDrive';
import { useAppSettingLists } from '../../hooks/useAppSettings';
import { useDialog } from '../../context/DialogContext';

interface DocumentTypeItem {
  value: string;
  label: string;
}

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 25 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getDrivePreviewUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${fileId}/preview`;
}

/** Default display text derived from a URL — hostname + path, no protocol/www. */
export function defaultLinkText(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.replace(/^www\./, '');
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    return `${host}${path}`;
  } catch {
    return trimmed;
  }
}

/** Normalize a typed URL so bare domains still open correctly. */
function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  return trimmed.includes('://') ? trimmed : `https://${trimmed}`;
}

function AttachmentPreviewModal({
  attachment,
  onClose,
}: {
  attachment: EntityAttachment;
  onClose: () => void;
}) {
  const isDrive = attachment.externalSource === 'GOOGLE_DRIVE';
  const isLink = attachment.externalSource === 'LINK';
  const isPdf = attachment.mimeType === 'application/pdf';
  const isImage = attachment.mimeType?.startsWith('image/');

  let previewContent: React.ReactNode;
  if (isLink && attachment.externalUrl) {
    previewContent = (
      <div className="w-full h-full flex flex-col">
        <iframe
          src={attachment.externalUrl}
          className="w-full flex-1"
          title={attachment.fileName || 'Link preview'}
          // No allow-same-origin: arbitrary external URLs must not run with
          // their origin's cookies/storage (or the app's own origin).
          sandbox="allow-scripts"
        />
        <div className="flex items-center justify-center gap-2 px-4 py-2 bg-white border-t border-gray-200 text-xs text-gray-500">
          <span>Some sites block embedded previews.</span>
          <a
            href={attachment.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink size={12} /> Open link
          </a>
        </div>
      </div>
    );
  } else if (isDrive && attachment.externalFileId) {
    previewContent = (
      <iframe
        src={getDrivePreviewUrl(attachment.externalFileId)}
        className="w-full h-full"
        title={attachment.fileName || 'Drive document'}
        allow="autoplay"
      />
    );
  } else if (isPdf && attachment.downloadUrl) {
    previewContent = (
      <iframe
        src={attachment.downloadUrl}
        className="w-full h-full"
        title={attachment.fileName || ''}
      />
    );
  } else if (isImage && attachment.downloadUrl) {
    previewContent = (
      <img
        src={attachment.downloadUrl}
        alt={attachment.fileName || ''}
        className="max-w-full max-h-full object-contain"
      />
    );
  } else {
    previewContent = (
      <div className="text-center p-8">
        <FileText size={48} className="mx-auto mb-3 text-gray-400" />
        <p className="text-sm text-gray-600">Preview not available for this file type.</p>
        {attachment.downloadUrl && (
          <a
            href={attachment.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:underline mt-2 inline-block"
          >
            Download to view
          </a>
        )}
        {isDrive && attachment.externalUrl && (
          <a
            href={attachment.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:underline mt-2 inline-block"
          >
            Open in Drive
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full h-full sm:h-[90vh] sm:max-w-4xl sm:mx-4 bg-white sm:rounded-lg shadow-xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {isDrive && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex-shrink-0">
                Drive
              </span>
            )}
            {isLink && (
              <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full flex-shrink-0">
                Link
              </span>
            )}
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              {attachment.fileName || 'Document'}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {attachment.downloadUrl && !isDrive && (
              <a
                href={attachment.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-600 p-1"
              >
                <Download size={18} />
              </a>
            )}
            {(isDrive || isLink) && attachment.externalUrl && (
              <a
                href={attachment.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-primary-600 p-1"
              >
                <Link size={18} />
              </a>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
          {previewContent}
        </div>
      </div>
    </div>
  );
}

interface EntityAttachmentSectionProps {
  entityType: string;
  entityId: string;
  title?: string;
}

export function EntityAttachmentSection({
  entityType,
  entityId,
  title = 'Attachments',
}: EntityAttachmentSectionProps) {
  const { data: attachments = [], isLoading } = useEntityAttachments(entityType, entityId);
  const createAttachment = useCreateEntityAttachment();
  const updateAttachment = useUpdateEntityAttachment();
  const deleteAttachment = useDeleteEntityAttachment();
  const { data: settingLists = [] } = useAppSettingLists();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [previewItem, setPreviewItem] = useState<EntityAttachment | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [showDriveOptions, setShowDriveOptions] = useState(false);
  const [pendingDocType, setPendingDocType] = useState<{ id: string; value: string } | null>(null);
  // Link add/edit form — text defaults from the URL until the user edits it.
  const [linkForm, setLinkForm] = useState<{
    id: string | null;
    url: string;
    text: string;
    textTouched: boolean;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { pickAndLink, pickAndDownload, status: driveStatus } = useGoogleDrive();
  const { confirm } = useDialog();

  const documentTypeList = settingLists.find((l) => l.listCode === 'document_type');
  const documentTypes = (documentTypeList?.items as DocumentTypeItem[] | undefined) ?? [];

  const handleDocTypeChange = useCallback(
    (attachmentId: string, value: string) => {
      setPendingDocType(null);
      updateAttachment.mutate({ id: attachmentId, input: { documentType: value || null } });
    },
    [updateAttachment],
  );

  const handleUpload = useCallback(
    async (file: File) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError('Only PDF and image files are supported.');
        return;
      }
      if (file.size > MAX_SIZE) {
        setUploadError('File exceeds the 25 MB limit.');
        return;
      }
      setUploadError('');
      setIsUploading(true);
      try {
        const { uploadUrl, s3Key } = await getAttachmentUploadUrl(file.name, file.type);
        await uploadFileToS3(uploadUrl, file);
        const input: CreateEntityAttachmentInput = {
          entityType,
          entityId,
          fileName: file.name,
          s3Key,
          mimeType: file.type,
          fileSizeBytes: file.size,
        };
        const created = await createAttachment.mutateAsync(input);
        if (documentTypes.length > 0) {
          setPendingDocType({ id: created.id, value: '' });
        }
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      } finally {
        setIsUploading(false);
      }
    },
    [entityType, entityId, createAttachment, documentTypes],
  );

  const handleDriveDownload = useCallback(async () => {
    setShowDriveOptions(false);
    setUploadError('');
    const result = await pickAndDownload();
    if (result) {
      const input: CreateEntityAttachmentInput = result.s3Key
        ? {
            entityType,
            entityId,
            fileName: result.name,
            s3Key: result.s3Key,
            mimeType: result.mimeType,
            fileSizeBytes: result.fileSizeBytes,
          }
        : {
            entityType,
            entityId,
            fileName: result.name,
            mimeType: result.mimeType,
            externalUrl: result.externalUrl,
            externalSource: 'GOOGLE_DRIVE',
            externalFileId: result.externalFileId,
          };
      const created = await createAttachment.mutateAsync(input);
      if (documentTypes.length > 0) {
        setPendingDocType({ id: created.id, value: '' });
      }
    }
  }, [entityType, entityId, pickAndDownload, createAttachment, documentTypes]);

  const handleDriveLink = useCallback(async () => {
    setShowDriveOptions(false);
    setUploadError('');
    const result = await pickAndLink();
    if (result) {
      const input: CreateEntityAttachmentInput = {
        entityType,
        entityId,
        fileName: result.name,
        mimeType: result.mimeType,
        externalUrl: result.url,
        externalSource: 'GOOGLE_DRIVE',
        externalFileId: result.fileId,
      };
      const created = await createAttachment.mutateAsync(input);
      if (documentTypes.length > 0) {
        setPendingDocType({ id: created.id, value: '' });
      }
    }
  }, [entityType, entityId, pickAndLink, createAttachment, documentTypes]);

  const handleDelete = useCallback(
    async (id: string) => {
      const confirmed = await confirm({
        title: 'Remove Attachment',
        message: 'Remove this attachment?',
        confirmLabel: 'Remove',
      });
      if (confirmed) {
        deleteAttachment.mutate({ id, entityType, entityId });
      }
    },
    [deleteAttachment, entityType, entityId, confirm],
  );

  const handleSaveLink = useCallback(async () => {
    if (!linkForm || !linkForm.url.trim()) return;
    const url = normalizeUrl(linkForm.url);
    const text = linkForm.text.trim() || defaultLinkText(linkForm.url);
    setUploadError('');
    try {
      if (linkForm.id) {
        await updateAttachment.mutateAsync({
          id: linkForm.id,
          input: { fileName: text, externalUrl: url },
        });
      } else {
        await createAttachment.mutateAsync({
          entityType,
          entityId,
          fileName: text,
          externalUrl: url,
          externalSource: 'LINK',
        });
      }
      setLinkForm(null);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to save link.');
    }
  }, [linkForm, entityType, entityId, createAttachment, updateAttachment]);

  const isProcessing =
    isUploading ||
    driveStatus === 'downloading' ||
    driveStatus === 'authorizing' ||
    driveStatus === 'picking' ||
    driveStatus === 'loading-sdk';

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <h4 className="text-xs font-semibold text-gray-700 mb-3">{title}</h4>

      <div className="flex gap-2 mb-3 flex-wrap">
        <div
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files[0]);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onClick={() => inputRef.current?.click()}
          className={`flex-1 min-w-[140px] border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-primary-400'
          }`}
        >
          {isProcessing ? (
            <div className="flex items-center justify-center gap-1.5">
              <Loader2 size={14} className="animate-spin text-primary-600" />
              <span className="text-xs text-gray-600">
                {driveStatus === 'downloading'
                  ? 'Downloading...'
                  : driveStatus === 'picking'
                    ? 'Picking...'
                    : 'Uploading...'}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-1.5">
              <Upload size={14} className="text-gray-400" />
              <span className="text-xs text-gray-600">
                Upload file <span className="text-primary-600">or drag</span>
              </span>
            </div>
          )}
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,image/jpeg,image/png,image/webp"
            onChange={(e) => {
              if (e.target.files?.[0]) {
                handleUpload(e.target.files[0]);
                e.target.value = '';
              }
            }}
            className="hidden"
          />
        </div>

        <div className="relative">
          <button
            onClick={() => setShowDriveOptions(!showDriveOptions)}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors h-full"
          >
            <HardDrive size={14} className="text-blue-500" />
            Google Drive
          </button>
          {showDriveOptions && (
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
              <button
                onClick={handleDriveDownload}
                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
              >
                <Download size={12} /> Download as PDF to S3
              </button>
              <button
                onClick={handleDriveLink}
                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2 rounded-b-lg border-t border-gray-100"
              >
                <Link size={12} /> Link only (keep in Drive)
              </button>
            </div>
          )}
        </div>

        <button
          onClick={() => setLinkForm({ id: null, url: '', text: '', textTouched: false })}
          className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50 transition-colors"
          data-testid="add-link-button"
        >
          <Link size={14} className="text-emerald-600" />
          Add Link
        </button>
      </div>

      {linkForm && (
        <div
          className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2"
          data-testid="link-form"
        >
          <div>
            <label className="block text-xs text-gray-500 mb-1">URL</label>
            <input
              type="url"
              value={linkForm.url}
              onChange={(e) =>
                setLinkForm(
                  (f) =>
                    f && {
                      ...f,
                      url: e.target.value,
                      text: f.textTouched ? f.text : defaultLinkText(e.target.value),
                    },
                )
              }
              placeholder="https://..."
              aria-label="Link URL"
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Display Text</label>
            <input
              type="text"
              value={linkForm.text}
              onChange={(e) =>
                setLinkForm((f) => f && { ...f, text: e.target.value, textTouched: true })
              }
              placeholder="Link text"
              aria-label="Link display text"
              className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setLinkForm(null)}
              className="px-2.5 py-1.5 text-xs text-gray-600 hover:bg-gray-100 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveLink}
              disabled={
                !linkForm.url.trim() || createAttachment.isPending || updateAttachment.isPending
              }
              className="px-2.5 py-1.5 text-xs bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
              data-testid="save-link-button"
            >
              {linkForm.id ? 'Save Link' : 'Add Link'}
            </button>
          </div>
        </div>
      )}

      {uploadError && <p className="text-xs text-red-600 mb-2">{uploadError}</p>}

      {isLoading ? (
        <p className="text-xs text-gray-400">Loading...</p>
      ) : attachments.length === 0 ? (
        <p className="text-xs text-gray-400">No attachments</p>
      ) : (
        <ul className="space-y-1.5">
          {attachments.map((att) => (
            <li key={att.id} className="bg-gray-50 rounded-md px-2.5 py-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {att.externalSource === 'LINK' ? (
                    <Link size={14} className="text-emerald-600 flex-shrink-0" />
                  ) : att.externalSource === 'GOOGLE_DRIVE' ? (
                    <HardDrive size={14} className="text-blue-500 flex-shrink-0" />
                  ) : att.mimeType?.startsWith('image/') ? (
                    <Image size={14} className="text-blue-500 flex-shrink-0" />
                  ) : (
                    <FileText size={14} className="text-red-500 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    {att.externalSource === 'LINK' && att.externalUrl ? (
                      <a
                        href={att.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs font-medium text-primary-700 hover:underline truncate block"
                        title={att.externalUrl}
                      >
                        {att.fileName || att.externalUrl}
                      </a>
                    ) : (
                      <p className="text-xs font-medium text-gray-900 truncate">
                        {att.fileName || 'Attachment'}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      {att.externalSource === 'LINK'
                        ? 'Link'
                        : att.externalSource === 'GOOGLE_DRIVE'
                          ? 'Google Drive'
                          : att.fileSizeBytes
                            ? formatFileSize(att.fileSizeBytes)
                            : ''}
                      {' · '}
                      {new Date(att.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0">
                  {documentTypes.length > 0 && (
                    <button
                      onClick={() =>
                        setPendingDocType({ id: att.id, value: att.documentType ?? '' })
                      }
                      className="text-gray-400 hover:text-primary-600 p-1"
                      title="Set document type"
                    >
                      <Tag size={13} />
                    </button>
                  )}
                  {att.externalSource === 'LINK' && (
                    <button
                      onClick={() =>
                        setLinkForm({
                          id: att.id,
                          url: att.externalUrl ?? '',
                          text: att.fileName ?? '',
                          textTouched: true,
                        })
                      }
                      className="text-gray-400 hover:text-primary-600 p-1"
                      title="Edit link"
                      data-testid={`edit-link-${att.id}`}
                    >
                      <Pencil size={13} />
                    </button>
                  )}
                  {(att.externalSource === 'LINK'
                    ? !!att.externalUrl
                    : att.externalSource === 'GOOGLE_DRIVE'
                      ? !!att.externalFileId
                      : !!att.downloadUrl &&
                        (att.mimeType === 'application/pdf' ||
                          !!att.mimeType?.startsWith('image/'))) && (
                    <button
                      onClick={() => setPreviewItem(att)}
                      className="text-gray-400 hover:text-primary-600 p-1"
                      title="Preview"
                    >
                      <Eye size={13} />
                    </button>
                  )}
                  <button
                    onClick={() => void handleDelete(att.id)}
                    className="text-gray-400 hover:text-red-600 p-1"
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {att.documentType && (
                <div className="mt-1 ml-6">
                  <span className="text-[10px] px-1.5 py-0.5 bg-primary-50 text-primary-700 rounded-full font-medium">
                    {documentTypes.find((t) => t.value === att.documentType)?.label ??
                      att.documentType}
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {pendingDocType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xs p-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">Set Document Type</h3>
            <select
              value={pendingDocType.value}
              onChange={(e) => setPendingDocType((p) => p && { ...p, value: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">— No type —</option>
              {documentTypes.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <button
                onClick={() => handleDocTypeChange(pendingDocType.id, pendingDocType.value)}
                className="flex-1 py-2 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white"
              >
                Save
              </button>
              <button
                onClick={() => setPendingDocType(null)}
                className="flex-1 py-2 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 text-gray-700"
              >
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {previewItem && (
        <AttachmentPreviewModal attachment={previewItem} onClose={() => setPreviewItem(null)} />
      )}
    </div>
  );
}
